import { jsx as _jsx } from "react/jsx-runtime";
import { createBrowserRouter } from '@rocketlane/rocket-router';
import { lazy } from 'react';
import { App } from 'app';
import { allTaskBoardsPageRoute, allTasksRoute } from 'components/pages/all-tasks/all-tasks-routes';
import { NotFound } from './404';
import { agentBuilderRoutes } from './agent-builder/agent-builder-routes';
import { analyticsAgentRoutes } from './analytics-agent/analytics-agent-routes';
import { AttachmentWorkspaceRedirect } from './attachment-workspace-redirect';
import { accountRoutes } from './customer-portfolio/customer-portfolio-routes';
import { LegacySignalsAiAnalyticsConversationRedirect, LegacySignalsAiAnalyticsIndexRedirect } from './customer-portfolio/signals-ai-analytics-legacy-redirects';
import { dashboardRoutes } from './dashboard/dashboards-routes';
import { expenseRoutes } from './expenses/expense-routes';
import { ldsRoutes, marketplaceWidgetRoutes } from './marketplace/marketplace-routes';
import { nonProtectedRoutes } from './non-protected-routes';
import { projectRoutes } from './projects/projects-routes';
import { ProtectedRoutes } from './protected-routes';
import { reportsRoutes } from './reports/reports-routes';
import { resourceManagementRoutes } from './resource-management/resource-management-routes';
import { revenueEntryRoutes } from './revenue-entries/revenue-entries-routes';
import { NonProtectedRoutesLayout } from './route-navigators';
import { SecureRoute } from './route-with-permission-check';
import { ROUTE_PATHS } from './routes-paths';
import { settingsRoutes } from './settings/settings-routes';
import { nitroBrewRoutes } from './nitro-brew/nitro-brew-routes';
import { signalAgentsRoutes } from './signal-agents/signal-agents-routes';
import { skillBuilderRoutes } from './skill-builder/skill-builder-routes';
import { sprintRoutes } from './sprint-planning';
import { templateRoutes } from './templates/templates-routes';
import { timesheetRoutes } from './timesheets/timesheets-routes';
import { AttachmentWorkspacePage, AutomationLogsByAutomationIdPage, FormPreviewPage, Home, LazyFeatureFlagList, LazyRespSpaceTabPage, LazyRespStatusUpdate, LazyRespTaskDesc, LazyTaskDetailsPageResponsive, NotificationsPage, OverlayComparisonPage, SideBySideComparePage } from './vendor/lazy-imports';
const CpPreviewWithoutProject = /*#__PURE__*/ lazy(()=>import('components/organisms/customer-portal/builder-components/tabs/design-tab/cp-preview-without-project').then((m)=>({
            default: m.CpPreviewWithoutProject
        })));
const formPreviewRoutes = {
    // path: 'form-preview/:formTemplateId',
    path: `${ROUTE_PATHS.FORM_PREVIEW}/${ROUTE_PATHS.FORM_TEMPLATE_ID}`,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        children: /*#__PURE__*/ _jsx(FormPreviewPage, {})
    })
};
const attachmentRoutes = [
    {
        path: `${ROUTE_PATHS.ATTACHMENTS}/${ROUTE_PATHS.ATTACHMENT_ID}`,
        element: /*#__PURE__*/ _jsx(AttachmentWorkspaceRedirect, {})
    },
    {
        // path: 'attachments/:attachmentId/workspace',
        path: `${ROUTE_PATHS.ATTACHMENTS}/${ROUTE_PATHS.ATTACHMENT_ID}/${ROUTE_PATHS.WORKSPACE}`,
        element: /*#__PURE__*/ _jsx(SecureRoute, {
            children: /*#__PURE__*/ _jsx(AttachmentWorkspacePage, {})
        })
    },
    {
        // path: 'attachments/:attachmentId/workspace/sidebyside/:compareAttachmentId',
        path: `${ROUTE_PATHS.ATTACHMENTS}/${ROUTE_PATHS.ATTACHMENT_ID}/${ROUTE_PATHS.WORKSPACE}/${ROUTE_PATHS.SIDE_BY_SIDE}/${ROUTE_PATHS.COMPARE_ATTACHMENT_ID}`,
        element: /*#__PURE__*/ _jsx(SecureRoute, {
            children: /*#__PURE__*/ _jsx(SideBySideComparePage, {})
        })
    },
    {
        // path: 'attachments/:attachmentId/workspace/overlay/:compareAttachmentId',
        path: `${ROUTE_PATHS.ATTACHMENTS}/${ROUTE_PATHS.ATTACHMENT_ID}/${ROUTE_PATHS.WORKSPACE}/${ROUTE_PATHS.OVERLAY}/${ROUTE_PATHS.COMPARE_ATTACHMENT_ID}`,
        element: /*#__PURE__*/ _jsx(SecureRoute, {
            children: /*#__PURE__*/ _jsx(OverlayComparisonPage, {})
        })
    }
];
const liveCustomerPortalPreviewRoutes = [
    {
        // path: 'live-customer-portal-preview/:projectId',
        path: `${ROUTE_PATHS.LIVE_CUSTOMER_PORTAL_PREVIEW}/${ROUTE_PATHS.PROJECT_ID}`,
        caseSensitive: true,
        // element: <CpPreviewRoute />,
        async lazy () {
            const { CpPreviewRoute } = await import('components/organisms/customer-portal/builder-components/tabs/design-tab/cp-preview-route');
            return {
                element: /*#__PURE__*/ _jsx(SecureRoute, {
                    children: /*#__PURE__*/ _jsx(CpPreviewRoute, {})
                })
            };
        }
    },
    {
        // path: 'live-customer-portal-preview',
        path: ROUTE_PATHS.LIVE_CUSTOMER_PORTAL_PREVIEW,
        caseSensitive: true,
        element: /*#__PURE__*/ _jsx(SecureRoute, {
            rule: "TEMPLATE:READ",
            children: /*#__PURE__*/ _jsx(CpPreviewWithoutProject, {})
        })
    }
];
const responsiveSpaceTabRoutes = {
    // path: 'responsive/:projectId/:spaceId/:spaceTabId',
    path: `${ROUTE_PATHS.RESPONSIVE}/${ROUTE_PATHS.PROJECT_ID}/${ROUTE_PATHS.PROJECT_SPACE_ID}/${ROUTE_PATHS.PROJECT_SPACE_TAB_ID}`,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        children: /*#__PURE__*/ _jsx(LazyRespSpaceTabPage, {})
    })
};
const responsiveProjectUpdateRoutes = {
    // path: 'project-updates-responsive/:projectId/:statusUpdateId',
    path: `${ROUTE_PATHS.PROJECT_UPDATE_RESPONSIVE}/${ROUTE_PATHS.PROJECT_ID}/${ROUTE_PATHS.PROJECT_STATUS_UPDATE_ID}`,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        children: /*#__PURE__*/ _jsx(LazyRespStatusUpdate, {})
    })
};
const responsiveTaskDescRoutes = {
    // path: 'responsive-task-desc/:projectId/:taskId',
    path: `${ROUTE_PATHS.RESPONSIVE_TASK_DESCRIPTION}/${ROUTE_PATHS.PROJECT_ID}/${ROUTE_PATHS.TASK_ID}`,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        children: /*#__PURE__*/ _jsx(LazyRespTaskDesc, {})
    })
};
const responsiveProjectTaskDetailRoutes = {
    // path: 'projects/:projectId/taskDetails/:taskId',
    path: `${ROUTE_PATHS.PROJECTS}/${ROUTE_PATHS.PROJECT_ID}/${ROUTE_PATHS.TASK_DETAILS}/${ROUTE_PATHS.TASK_ID}`,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        children: /*#__PURE__*/ _jsx(LazyTaskDetailsPageResponsive, {})
    })
};
const customAutomationLogsForAutomationIdRoutes = {
    // path: 'custom-automation-logs/automation/:automationId',
    path: `${ROUTE_PATHS.CUSTOM_AUTOMATION_LOGS}/${ROUTE_PATHS.AUTOMATION}/${ROUTE_PATHS.AUTOMATION_ID}`,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        children: /*#__PURE__*/ _jsx(AutomationLogsByAutomationIdPage, {})
    })
};
const featureFlagRoutes = {
    // path: 'feature-flags',
    path: `${ROUTE_PATHS.FEATURE_FLAGS}`,
    element: /*#__PURE__*/ _jsx(SecureRoute, {
        children: /*#__PURE__*/ _jsx(LazyFeatureFlagList, {})
    })
};
const signalsAiAnalyticsLegacyRoutes = [
    {
        path: `${ROUTE_PATHS.SIGNALS_AI_ANALYTICS}/${ROUTE_PATHS.CONVERSATION_ID}`,
        element: /*#__PURE__*/ _jsx(LegacySignalsAiAnalyticsConversationRedirect, {})
    },
    {
        path: `${ROUTE_PATHS.SIGNALS_AI_ANALYTICS}`,
        element: /*#__PURE__*/ _jsx(LegacySignalsAiAnalyticsIndexRedirect, {})
    }
];
const homeRoutes = {
    path: ROUTE_PATHS.EMPTY,
    element: /*#__PURE__*/ _jsx(Home, {})
};
const notificationRoutes = {
    path: `${ROUTE_PATHS.NOTIFICATIONS}`,
    element: /*#__PURE__*/ _jsx(NotificationsPage, {})
};
const customerNotificationRoutes = {
    path: ROUTE_PATHS.SETTINGS_NOTIFICATIONS,
    async lazy () {
        const { default: CustomerRoutes } = await import('./customer/customer');
        return {
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                children: /*#__PURE__*/ _jsx(CustomerRoutes, {})
            })
        };
    }
};
const customerCompanyTasksRoutes = {
    // path: 'company-tasks/*',
    path: `${ROUTE_PATHS.COMPANY_TASKS}/${ROUTE_PATHS.MATCH_ALL}`,
    async lazy () {
        const { default: CustomerRoutes } = await import('./customer/customer');
        return {
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                children: /*#__PURE__*/ _jsx(CustomerRoutes, {})
            })
        };
    }
};
const invoiceRoutes = {
    path: `${ROUTE_PATHS.INVOICES}/${ROUTE_PATHS.MATCH_ALL}`,
    async lazy () {
        const { InvoicesRoute, InvoicesRoutesLayout } = await import('./invoices');
        return {
            element: /*#__PURE__*/ _jsx(InvoicesRoutesLayout, {
                children: /*#__PURE__*/ _jsx(SecureRoute, {
                    children: /*#__PURE__*/ _jsx(InvoicesRoute, {})
                })
            })
        };
    }
};
const restOfTheCustomerRoutes = {
    // path: '*',
    path: ROUTE_PATHS.MATCH_ALL,
    async lazy () {
        const { default: CustomerRoutes } = await import('./customer/customer');
        return {
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                children: /*#__PURE__*/ _jsx(CustomerRoutes, {})
            })
        };
    }
};
const routes = [
    {
        path: ROUTE_PATHS.ROOT,
        element: /*#__PURE__*/ _jsx(App, {}),
        children: [
            {
                element: /*#__PURE__*/ _jsx(ProtectedRoutes, {}),
                children: [
                    homeRoutes,
                    notificationRoutes,
                    dashboardRoutes,
                    reportsRoutes,
                    ...analyticsAgentRoutes,
                    accountRoutes,
                    projectRoutes,
                    allTasksRoute,
                    allTaskBoardsPageRoute,
                    sprintRoutes,
                    resourceManagementRoutes,
                    timesheetRoutes,
                    expenseRoutes,
                    revenueEntryRoutes,
                    invoiceRoutes,
                    templateRoutes,
                    settingsRoutes,
                    ...agentBuilderRoutes,
                    ...skillBuilderRoutes,
                    ...nitroBrewRoutes,
                    ...signalAgentsRoutes,
                    ldsRoutes,
                    marketplaceWidgetRoutes,
                    customerNotificationRoutes,
                    customerCompanyTasksRoutes,
                    ...liveCustomerPortalPreviewRoutes,
                    ...attachmentRoutes,
                    formPreviewRoutes,
                    responsiveSpaceTabRoutes,
                    responsiveProjectUpdateRoutes,
                    responsiveTaskDescRoutes,
                    responsiveProjectTaskDetailRoutes,
                    customAutomationLogsForAutomationIdRoutes,
                    featureFlagRoutes,
                    ...signalsAiAnalyticsLegacyRoutes,
                    restOfTheCustomerRoutes
                ]
            },
            {
                element: /*#__PURE__*/ _jsx(NonProtectedRoutesLayout, {}),
                caseSensitive: true,
                children: nonProtectedRoutes
            },
            {
                path: ROUTE_PATHS.MATCH_ALL,
                element: /*#__PURE__*/ _jsx(NotFound, {})
            }
        ]
    }
];
export const router = createBrowserRouter(routes);
